import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Grid, TextField, MenuItem, Chip,
  LinearProgress, Stack
} from '@mui/material';
import {
  Assessment as ResultsIcon, ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { academyApi } from '../services/academyApi';
import { academyStore } from '../services/academyStore';
import { Quiz, QuizAttempt } from '../types/academy';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import GenericPage from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import { useSearchParams, useNavigate } from 'react-router-dom';

const fsx = {
  '& .MuiInputBase-root': { fontSize: '0.85rem' },
  '& .MuiInputLabel-root': { fontSize: '0.85rem' },
};

export const QuizAttemptsPage: React.FC = () => {
  const { t } = useLanguage();
  
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuizId = Number(searchParams.get('qiz_id')) || 0;

  const [quizzes, setQuizzes] = useState<Quiz[]>(() => academyStore.getQuizzes());
  const [selectedQuizId, setSelectedQuizId] = useState<number>(initialQuizId);
  const [attempts, setAttempts] = useState<QuizAttempt[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSelectedQuizId(Number(searchParams.get('qiz_id')) || 0);
  }, [searchParams]);

  useEffect(() => {
    const sync = () => {
      setQuizzes(academyStore.getQuizzes());
      const allAttempts = academyStore.getQuizAttempts();
      if (selectedQuizId) {
        setAttempts(allAttempts.filter(a => a.quiz_id === selectedQuizId));
      } else {
        setAttempts(allAttempts);
      }
    };
    sync();
    return academyStore.subscribe(sync);
  }, [selectedQuizId]);

  const handleQuizChange = (quizId: number) => {
    setSearchParams(quizId ? { qiz_id: String(quizId) } : {});
  };

  const avgScore = attempts.length
    ? Math.round((attempts.reduce((s, a) => s + (a.total > 0 ? (a.score / a.total) : 0), 0) / attempts.length) * 100)
    : 0;

  return (
    <GenericPage
      title={t('academy.quiz_results.title')}
      icon={<ResultsIcon />}
      footerEnabled={false}
      additionalActions={
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/Academy/quizzes')} sx={{ borderRadius: 2 }}>
          {t('academy.quiz_results.back_to_quizzes')}
        </Button>
      }
    >
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, mb: 3, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('academy.quiz_results.select_quiz')}
              select
              size="small"
              fullWidth
              sx={fsx}
              value={selectedQuizId || ''}
              onChange={e => handleQuizChange(Number(e.target.value))}
            >
              <MenuItem value="" disabled>{t('academy.quiz_results.select_quiz_placeholder')}</MenuItem>
              {quizzes.map(q => (
                <MenuItem key={q.id} value={q.id}>{q.title} ({q.code})</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            {selectedQuizId ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={t('academy.quiz_results.count', attempts.length)} color="primary" variant="outlined" />
                <Chip label={t('academy.quiz_results.avg', `${avgScore}%`)} color={avgScore >= 60 ? 'success' : 'warning'} variant="outlined" />
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">{t('academy.quiz_results.no_quiz_selected')}</Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      {!selectedQuizId ? (
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderRadius: 3, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc' }}>
          <Typography variant="subtitle1" color="text.secondary">
            {t('academy.quiz_results.no_quiz_selected')}
          </Typography>
        </Paper>
      ) : loading ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('common.loading')}</Typography>
        </Box>
      ) : (
        <GenericTable
          columns={[
            { id: 'ref_code', label: t('academy.quiz_results.table.ref'), minWidth: 160 },
            { id: 'full_name', label: t('academy.quiz_results.table.name'), minWidth: 160 },
            { id: 'phone', label: t('academy.quiz_results.table.phone'), minWidth: 130 },
            { id: 'email', label: t('academy.quiz_results.table.email'), minWidth: 160 },
            {
              id: 'score',
              label: t('academy.quiz_results.table.score'),
              minWidth: 160,
              format: (val: any, row?: any) => (
                <Box sx={{ minWidth: 120 }}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" fontWeight={700}>{val}/{row?.total}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row?.total > 0 ? Math.round((val / row.total) * 100) : 0}%
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={row?.total > 0 ? (val / row.total) * 100 : 0}
                    color={row?.total > 0 && val / row.total >= 0.6 ? 'success' : 'warning'}
                    sx={{ borderRadius: 2, height: 6 }}
                  />
                </Box>
              )
            },
            {
              id: 'level_code',
              label: t('academy.quiz_results.table.level'),
              minWidth: 100,
              format: (val: any) => val ? <Chip label={val} color="info" size="small" variant="outlined" /> : '-'
            },
            {
              id: 'duration_seconds',
              label: t('academy.quiz_results.table.duration'),
              minWidth: 110,
              format: (val: any) => {
                if (!val) return '-';
                const m = Math.floor(val / 60);
                const s = val % 60;
                return `${m}:${String(s).padStart(2, '0')}`;
              }
            },
            { id: 'last_updt', label: t('academy.quiz_results.table.date'), minWidth: 150 },
          ]}
          data={attempts}
          searchPlaceholder={t('academy.quiz_results.search_placeholder')}
          maxHeight="calc(100vh - 420px)"
        />
      )}
    </GenericPage>
  );
};

export default QuizAttemptsPage;
